/**
 * TerraMatrix Universal Data Platform - TemplateExecutionModal Component
 *
 * Modal dialog for executing a query template with variable overrides.
 * Shows a dynamic form based on the template's variable definitions.
 *
 * Phase 5: Data Page UI Integration
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { VariableForm } from './VariableForm';
import { useQueryTemplate } from '@/hooks/data/useQueryTemplate';
import type { QueryTemplate, TemplateExecutionResult } from '@/services/api/queryTemplateApi';

interface TemplateExecutionModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The template to execute */
  template: QueryTemplate | null;
  /** Callback when execution completes successfully */
  onExecuted?: (result: TemplateExecutionResult) => void;
}

export const TemplateExecutionModal: React.FC<TemplateExecutionModalProps> = ({
  open,
  onClose,
  template,
  onExecuted,
}) => {
  const [variables, setVariables] = useState<Record<string, unknown>>({});

  // Initialize variables with defaults when template changes
  useEffect(() => {
    if (template?.variables) {
      const defaults: Record<string, unknown> = {};
      Object.entries(template.variables).forEach(([key, def]) => {
        if (def.default !== undefined) {
          defaults[key] = def.default;
        }
      });
      setVariables(defaults);
    }
  }, [template]);

  // Use the query template hook
  const {
    data,
    metadata,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQueryTemplate({
    templateId: open && template ? template.id : undefined,
    variables,
    enabled: false, // Don't auto-fetch, wait for manual execution
  });

  const handleExecute = async () => {
    try {
      const result = await refetch();
      if (result.data) {
        onExecuted?.({
          data: result.data.data || [],
          metadata: result.data.metadata || {
            templateId: template?.id || '',
            templateName: template?.name || '',
            resolvedVariables: variables,
            executionTimeMs: 0,
            rowCount: result.data.data?.length || 0,
            cached: false,
          },
        });
      }
    } catch (err) {
      console.error('Failed to execute template:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading && !isFetching) {
      onClose();
    }
  };

  if (!template) return null;

  const hasVariables = Object.keys(template.variables || {}).some(
    (key) => template.variables[key].type !== 'system'
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            Execute Template
          </DialogTitle>
          <DialogDescription>{template.name}</DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-2 space-y-4">
          {/* Template Description */}
          {template.description && (
            <div className="p-3 rounded-lg bg-muted/50 border border-border">
              <p className="text-sm text-muted-foreground">{template.description}</p>
            </div>
          )}

          {/* Variable Form */}
          {hasVariables ? (
            <div>
              <h4 className="text-sm font-medium mb-3">Configure Variables</h4>
              <VariableForm
                variables={template.variables}
                values={variables}
                onChange={setVariables}
                disabled={isLoading || isFetching}
              />
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-sm text-muted-foreground">
                This template has no configurable variables.
              </p>
            </div>
          )}

          {/* Results Preview (if executed) */}
          {data && data.length > 0 && (
            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Results ({metadata?.rowCount || data.length} rows)
              </h4>
              <div className="max-h-48 overflow-auto rounded-lg border border-border">
                <pre className="p-3 text-xs">
                  {JSON.stringify(data.slice(0, 5), null, 2)}
                  {data.length > 5 && (
                    <span className="text-muted-foreground">
                      {'\n'}... and {data.length - 5} more rows
                    </span>
                  )}
                </pre>
              </div>
              {metadata?.executionTimeMs && (
                <p className="text-xs text-muted-foreground mt-2">
                  Executed in {metadata.executionTimeMs}ms
                  {metadata.cached && ' (cached)'}
                </p>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm text-destructive">{error.message}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={handleClose}
            disabled={isLoading || isFetching}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-border
                       hover:bg-muted transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {data ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleExecute}
            disabled={isLoading || isFetching}
            className="px-4 py-2 text-sm font-medium rounded-lg
                       bg-primary text-primary-foreground
                       hover:bg-primary/90 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center gap-2"
          >
            {isLoading || isFetching ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Executing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                {data ? 'Re-Execute' : 'Execute'}
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateExecutionModal;
